import { useTheme } from '@mui/material';
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { actsAtom } from '@/atoms/acts';
import { tokenAtom } from '@/atoms/auth';
import { snackbarAtom } from '@/atoms/snackbar';
import { versionAtom } from '@/atoms/version';
import { companyIdAtom, projectIdAtom } from '@/atoms/workspace';
import { COLORS, getActTitle, paths } from '@/constants';
import { findActiveTextAtom } from '@/data/activeTextAtom';
import { Mode, type textAtomTypeId } from '@/enums';
import { usePrefValue } from '@/preferences';
import type { ILocation, SnackbarType, TextAtoms } from '@/types';

export const useVersion = () => useAtomValue(versionAtom);
export const useSnackbar = () => {
    const setSnackbar = useSetAtom(snackbarAtom);
    return (payload: SnackbarType) => setSnackbar(prev => ({ ...prev, ...payload }));
};
export const useToken = () => useAtomValue(tokenAtom);
export const useCompanyId = () => useAtomValue(companyIdAtom);
export const useProjectId = () => useAtomValue(projectIdAtom);

export const useOptionalSearchParam = (param: string) => {
    const [searchParams] = useSearchParams();

    return searchParams.get(param);
};

export const useOptionalSearchParamAsNumber = (param: string) => {
    const [searchParams] = useSearchParams();
    if (searchParams.get(param)) {
        return Number(searchParams.get(param));
    }

    return undefined;
};

export const useRequiredSearchParam = (param: string) => {
    const [searchParams] = useSearchParams();

    if (!searchParams.get(param)) {
        throw new Error(`Required search param "${param}" is missing`);
    }

    return Number(searchParams.get(param));
};

export const useRequiredAppSelector = (param: 'projectId' | 'companyId') => {
    const projectId = useAtomValue(projectIdAtom);
    const companyId = useAtomValue(companyIdAtom);
    const value = param === 'projectId' ? projectId : companyId;
    if (!value) {
        throw new Error(`Required app selector param "${param}" is missing`);
    }

    return Number(value);
};

export const useClientStorage = (name: string) => {
    const setItemStorage = (item: string) => {
        localStorage.setItem(name, item);
    };

    const getItemStorage = () => {
        return localStorage.getItem(name);
    };

    return {
        getItem: getItemStorage,
        setItem: setItemStorage,
    };
};

export const useActLabel = (order: number) => {
    const { t } = useTranslation();
    const acts = useAtomValue(actsAtom);
    const act = acts.find(act => act.value === order);
    if (act) return getActTitle(t, act);
    return 'TBD';
};

export const useGetLocationAndSubLocation = (location: ILocation | null) => {
    return useMemo(() => {
        let parent = '';
        let child = '';
        if (location) child = location.name;
        if (location?.parent) parent = location.parent.name;
        return parent ? `${parent}, ${child}` : child;
    }, [location]);
};

/** The atom of `typeId` an entity shows — the ACTIVE one of several
 *  (`data/activeTextAtom.ts`). A type nobody drafts answers as `find` did. */
export const useTextAtom = (textAtoms: TextAtoms[], typeId: textAtomTypeId) => {
    return useMemo(() => findActiveTextAtom(textAtoms, typeId), [textAtoms, typeId]);
};
export const useColorMode = () => {
    return usePrefValue('mode') === Mode.dark;
};

export function useGetLineColor() {
    const theme = useTheme();
    // useCallback is load-bearing: this is passed as a prop into memoized
    // components (notably FountainBlock) and compared by identity in their
    // areEqual. Without it, every render of any consumer broke memo and
    // forced the entire editor tree to reconcile.
    return useCallback(
        (code: string) => {
            const color = COLORS.find(item => item.code === code);
            if (color) return color.color;
            return theme.palette.mode === 'dark' ? theme.palette.info.contrastText : theme.palette.text.primary;
        },
        [theme],
    );
}

export const useRedirectOnFlag = (flag: string, enabled = true) => {
    const { flagsReady } = useFlagsStatus();
    const isAvailable = useFlag(flag);
    const navigate = useNavigate();

    useEffect(() => {
        if (flagsReady && !isAvailable && enabled) {
            navigate(paths.project.index);
        }
    }, [isAvailable, flagsReady, enabled, navigate]);
};
